/**
 * PauseOverlayRenderer — pause overlay with resume/main menu options.
 *
 * Supports standard (2-option) and tutorial (4-option) pause menus.
 * Extracted from MenuRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU, Z } from '../../constants';
import type { GameState } from '../../types';

const PAUSE_OPTIONS = ['RESUME GAME', 'MAIN MENU'];
const TUTORIAL_PAUSE_OPTIONS = ['RESUME', 'SKIP STEP', 'RESTART TUTORIAL', 'MAIN MENU'];

/** Max option count across all modes (used for pre-allocating UI elements). */
const MAX_OPTIONS = Math.max(PAUSE_OPTIONS.length, TUTORIAL_PAUSE_OPTIONS.length);

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

export class PauseOverlayRenderer {
    private pauseContainer: Container;
    private pauseOverlay: Graphics;
    private pauseTitle: Text;
    private pauseOptions: Text[] = [];
    private pauseHitRects: Graphics[] = [];
    private pauseBackButton: Text;
    private pauseBackHitRect: Graphics;
    private currentMode: string = '';

    constructor(
        uiContainer: Container,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ) {
        this.pauseContainer = new Container();
        this.pauseContainer.visible = false;
        this.pauseContainer.zIndex = Z.MENU_UI + 10;
        uiContainer.addChild(this.pauseContainer);

        const cx = WORLD.CANVAS_WIDTH / 2;

        // Dark overlay — tap outside = resume
        this.pauseOverlay = new Graphics();
        this.pauseOverlay.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        this.pauseOverlay.fill({ color: 0x000000, alpha: MENU.PAUSE_OVERLAY_ALPHA });
        this.pauseOverlay.eventMode = 'static';
        this.pauseOverlay.on('pointerdown', () => tapVirtualKey(Keys.Escape));
        this.pauseContainer.addChild(this.pauseOverlay);

        // "PAUSED" title
        this.pauseTitle = new Text({
            text: 'PAUSED',
            style: monoStyle(MENU.PAUSE_TITLE_SIZE, 0xffffff, true),
        });
        this.pauseTitle.anchor.set(0.5);
        this.pauseTitle.position.set(cx, 350);
        this.pauseContainer.addChild(this.pauseTitle);

        // Pre-allocate options with hit rects for max option count
        const pauseHitW = 400;
        const pauseHitH = 50;
        for (let i = 0; i < MAX_OPTIONS; i++) {
            const pauseY = 460 + i * 60;

            const hitRect = new Graphics();
            hitRect.position.set(cx - pauseHitW / 2, pauseY - pauseHitH / 2);
            hitRect.eventMode = 'static';
            hitRect.cursor = 'pointer';
            const pauseIdx = i;
            hitRect.on('pointerdown', (e: Event) => {
                e.stopPropagation();
                const s = getState();
                if (s) s.pauseSelectIndex = pauseIdx;
                tapVirtualKey(Keys.Space);
            });
            hitRect.on('pointerover', () => {
                const s = getState();
                if (s) s.pauseSelectIndex = pauseIdx;
            });
            this.pauseContainer.addChild(hitRect);
            this.pauseHitRects.push(hitRect);

            const opt = new Text({
                text: '',
                style: monoStyle(MENU.PAUSE_OPTION_SIZE, 0xffffff),
            });
            opt.anchor.set(0.5);
            opt.position.set(cx, pauseY);
            this.pauseContainer.addChild(opt);
            this.pauseOptions.push(opt);
        }

        // Back button
        this.pauseBackHitRect = new Graphics();
        this.pauseBackHitRect.eventMode = 'static';
        this.pauseBackHitRect.cursor = 'pointer';
        this.pauseBackHitRect.on('pointerdown', (e: Event) => {
            e.stopPropagation();
            tapVirtualKey(Keys.Escape);
        });
        this.pauseContainer.addChild(this.pauseBackHitRect);

        this.pauseBackButton = new Text({
            text: 'BACK (ESC)',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.pauseBackButton.anchor.set(0.5);
        this.pauseBackButton.position.set(cx, 460 + MAX_OPTIONS * 60 + 30);
        this.pauseContainer.addChild(this.pauseBackButton);
    }

    setVisible(v: boolean): void {
        this.pauseContainer.visible = v;
    }

    update(state: GameState, elapsed: number): void {
        const cx = WORLD.CANVAS_WIDTH / 2;
        const pauseHitW = 400;
        const pauseHitH = 50;
        const r = MENU.SELECTION_CORNER_RADIUS;

        // Determine which option set to use
        const isTutorial = state.modeState.mode === 'tutorial';
        const options = isTutorial ? TUTORIAL_PAUSE_OPTIONS : PAUSE_OPTIONS;

        // Update option labels if mode changed
        if (this.currentMode !== state.modeState.mode) {
            this.currentMode = state.modeState.mode;
            for (let i = 0; i < MAX_OPTIONS; i++) {
                if (i < options.length) {
                    this.pauseOptions[i].text = options[i];
                    this.pauseOptions[i].visible = true;
                    this.pauseHitRects[i].visible = true;
                } else {
                    this.pauseOptions[i].visible = false;
                    this.pauseHitRects[i].visible = false;
                }
            }
            // Reposition back button
            this.pauseBackButton.position.set(cx, 460 + options.length * 60 + 30);
        }

        for (let i = 0; i < options.length; i++) {
            const isSelected = i === state.pauseSelectIndex;
            this.pauseOptions[i].style.fill = isSelected ? 0xffffff : 0x666666;
            this.pauseOptions[i].style.fontSize = isSelected ? MENU.PAUSE_OPTION_SIZE + 4 : MENU.PAUSE_OPTION_SIZE;

            this.pauseHitRects[i].clear();
            if (isSelected) {
                this.pauseHitRects[i].roundRect(0, 0, pauseHitW, pauseHitH, r);
                this.pauseHitRects[i].fill({ color: 0xffffff, alpha: MENU.SELECTION_FILL_ALPHA });
                this.pauseHitRects[i].roundRect(0, 0, pauseHitW, pauseHitH, r);
                this.pauseHitRects[i].stroke({ color: 0xffffff, width: 1, alpha: 0.15 });
            }
            this.pauseHitRects[i].rect(0, 0, pauseHitW, pauseHitH);
            this.pauseHitRects[i].fill({ color: 0x000000, alpha: 0.001 });

            if (isSelected) {
                const pulse = 0.7 + 0.3 * Math.sin(elapsed * 4);
                this.pauseOptions[i].alpha = pulse;
            } else {
                this.pauseOptions[i].alpha = 1;
            }
        }

        // Back button
        this.pauseBackButton.visible = true;
        this.pauseBackHitRect.visible = true;
    }

    destroy(): void {
        this.pauseContainer.destroy({ children: true });
    }
}
